/**
 * ConsentTab - Manage member consent, webhooks, system tests, and election stats.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Chip, CircularProgress,
  Alert, Snackbar, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, Grid, Tabs, Tab, IconButton, Divider,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SyncIcon from "@mui/icons-material/Sync";
import api from "../../utils/api";

const cardSx = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const btnSx   = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

// Chapter 718 governs condos (§718.128 electronic voting), Chapter 720
// governs HOAs (§720.317). Rows saved before this chapter picker existed
// only have fs_718128_compliance=1 with no chapter — those fall back to
// "condo", the only chapter this system cited before this feature.
const complianceStatute = (election) => {
  const chapter = election?.compliance_chapter;
  if (chapter === "hoa") return "FS §720.317";
  if (chapter === "condo") return "FS §718.128";
  if (election?.fs_718128_compliance) return "FS §718.128";
  return null;
};

// ── Electronic Voting Consent List (synced from the membership system) ──────
const ElectronicVotingConsentList = () => {
  const [members, setMembers] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const r = await api.get(`/elections/consent-list?search=${encodeURIComponent(q)}&limit=5000`);
      setMembers(r.data?.members || []);
      setTotalRecords(r.data?.totalRecords || 0);
      setSyncStatus(r.data?.syncStatus || null);
    } catch {
      setSnack({ open:true, msg:"Failed to load consent list.", sev:"error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/elections/consent-list/sync");
      setSnack({ open:true, msg:`Synced — ${r.data?.count ?? 0} consented members.`, sev:"success" });
      await load(search);
    } catch (err) {
      setSnack({ open:true, msg: err.response?.data?.message || "Sync failed. Showing last cached list.", sev:"error" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={cardSx}>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:1.5, mb:1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>
            Electronic Voting Consent
          </Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Members who've consented to electronic voting in the membership system, synced automatically
            every 30 minutes and cached locally so this keeps working even if that connection drops.
            This list is also what "Send Invitations" shows when picking members.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={handleSync} disabled={syncing}
          sx={{ textTransform:"none", fontWeight:700, borderRadius:"10px", whiteSpace:"nowrap" }}>
          {syncing ? "Syncing..." : "Sync Now"}
        </Button>
      </Box>

      {syncStatus?.LastError && (
        <Alert severity="warning" sx={{ mb:1.5, borderRadius:2 }}>
          Last sync failed ({syncStatus.LastError}). Showing the last cached list
          {syncStatus.LastSuccessAt ? ` from ${new Date(syncStatus.LastSuccessAt).toLocaleString()}` : ""}.
        </Alert>
      )}

      <TextField size="small" placeholder="Search by name, member code, or email" value={search}
        onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter" && load(search)}
        sx={{ ...fieldSx, mb:1.5, maxWidth:360 }} />

      <Typography variant="caption" sx={{ display:"block", mb:1, color:"#6b778c" }}>
        {totalRecords} consented member{totalRecords!==1?"s":""}
        {members.length < totalRecords ? ` (showing first ${members.length} — search to narrow)` : ""}
        {syncStatus?.LastSuccessAt && !syncStatus?.LastError && ` • Last synced ${new Date(syncStatus.LastSuccessAt).toLocaleString()}`}
      </Typography>

      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden", maxHeight:360, overflowY:"auto" }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
            <TableRow>{["Member Code","Name","Email"].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {members.map(m=>(
              <TableRow key={m.ClubMemberCode} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{m.ClubMemberCode}</TableCell>
                <TableCell>{`${m.LastName || ""}${m.FirstName ? `, ${m.FirstName}` : ""}`.trim() || "N/A"}</TableCell>
                <TableCell sx={{ fontSize:12 }}>{m.EmailAddress || "N/A"}</TableCell>
              </TableRow>
            ))}
            {!loading && !members.length && <TableRow><TableCell colSpan={3} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No consented members found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Consent Management ─────────────────────────────────────────────
const ConsentManager = ({ electionId, election }) => {
  const [consents, setConsents] = useState([]);
  const [memberCode, setMemberCode] = useState("");
  const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    try { const r = await api.get(`/elections/${electionId}/consents`); setConsents(r.data||[]); }
    catch {}
  }, [electionId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (code, current) => {
    try {
      await api.post(`/elections/${electionId}/consents`, { member_code: code, consent_given: !current });
      setSnack({ open:true, msg:`Consent ${!current ? "granted" : "revoked"} for ${code}`, sev:"success" });
      load();
    } catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };

  const handleAdd = async () => {
    if (!memberCode.trim()) return;
    await handleToggle(memberCode.trim(), false);
    setMemberCode("");
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Track member consent for electronic voting{complianceStatute(election) ? ` as required by Florida ${complianceStatute(election)}` : ""}.
        Members must affirmatively consent before their votes count.
      </Typography>

      <Box sx={{ display:"flex", gap:1.5, mb:2 }}>
        <TextField size="small" label="Member Code" value={memberCode} onChange={e=>setMemberCode(e.target.value)}
          sx={{ ...fieldSx, minWidth:200 }} onKeyDown={e=>e.key==="Enter"&&handleAdd()} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Grant Consent</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
            <TableRow>{["Member Code","Status","Consent Date","Opt-out Date","Action"].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {consents.map(c=>(
              <TableRow key={c.Id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{c.MemberCode}</TableCell>
                <TableCell>
                  <Chip size="small" label={c.ConsentGiven?"Consented":"Opted Out"}
                    sx={{ fontWeight:700, fontSize:11, borderRadius:"8px",
                      bgcolor: c.ConsentGiven?"rgba(34,197,94,0.1)":"rgba(220,38,38,0.1)",
                      color: c.ConsentGiven?"#16a34a":"#dc2626" }} />
                </TableCell>
                <TableCell sx={{ fontSize:12 }}>{c.ConsentDate ? new Date(c.ConsentDate).toLocaleDateString() : "N/A"}</TableCell>
                <TableCell sx={{ fontSize:12 }}>{c.OptOutDate ? new Date(c.OptOutDate).toLocaleDateString() : "N/A"}</TableCell>
                <TableCell>
                  <Button size="small" onClick={()=>handleToggle(c.MemberCode, !!c.ConsentGiven)}
                    startIcon={c.ConsentGiven ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                    sx={{ textTransform:"none", fontSize:11, color: c.ConsentGiven?"#dc2626":"#16a34a" }}>
                    {c.ConsentGiven ? "Revoke" : "Restore"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!consents.length && <TableRow><TableCell colSpan={5} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No consent records.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Webhooks ───────────────────────────────────────────────────────
const WebhooksPanel = () => {
  const [hooks, setHooks] = useState([]);
  const [form,  setForm]  = useState({ url:"", events:"vote_submitted,election_closed", secret_key:"" });
  const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });
  const load = useCallback(async () => { const r = await api.get("/election-webhooks"); setHooks(r.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const handleAdd = async () => {
    if (!form.url) { setSnack({ open:true, msg:"URL required.", sev:"error" }); return; }
    try { await api.post("/election-webhooks", form); setForm({ url:"", events:"vote_submitted,election_closed", secret_key:"" }); setSnack({ open:true, msg:"Added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };
  return (
    <Box>
      <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>Send HTTP POST notifications when votes are submitted or elections close.</Alert>
      <Paper elevation={0} sx={cardSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Endpoint URL" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Events" value={form.events} onChange={e=>setForm(f=>({...f,events:e.target.value}))} sx={fieldSx} helperText="vote_submitted, election_closed, *" /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Secret" value={form.secret_key} onChange={e=>setForm(f=>({...f,secret_key:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Add Webhook</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["URL","Events","Active",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {hooks.map(h=>(
              <TableRow key={h.id} hover>
                <TableCell sx={{ fontSize:12, maxWidth:260 }}><Typography variant="caption" noWrap sx={{ display:"block", maxWidth:240 }}>{h.url}</Typography></TableCell>
                <TableCell sx={{ fontSize:11, color:"#6b778c" }}>{h.events}</TableCell>
                <TableCell><Chip size="small" label={h.is_active?"Active":"Off"} onClick={async()=>{ await api.patch(`/election-webhooks/${h.id}`,{is_active:!h.is_active}); load(); }} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:h.is_active?"rgba(34,197,94,0.1)":"rgba(0,0,0,0.06)", color:h.is_active?"#16a34a":"#6b778c", cursor:"pointer" }} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/election-webhooks/${h.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {!hooks.length && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No webhooks configured.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Election Stats ──────────────────────────────────────────────────
const StatsPanel = ({ electionId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/elections/${electionId}/consent-stats`); setStats(r.data); }
    catch {} finally { setLoading(false); }
  }, [electionId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <CircularProgress size={24} />;
  if (!stats) return <Alert severity="error" sx={{ borderRadius:2 }}>Failed to load stats.</Alert>;

  const items = [
    { label:"Total Votes Cast", value:stats.total_votes, color:"#2f63ad" },
    { label:"Invites Sent", value:stats.total_invites, color:"#374151" },
    { label:"Opened Invites", value:stats.opened_count, color:"#b45309" },
    { label:"Voted via Invite", value:stats.voted_count, color:"#16a34a" },
    { label:"Members Consented", value:stats.consented_count, color:"#7c3aed" },
    { label:"Share Link Votes", value:stats.share_link_votes, color:"#0369a1" },
    { label:"Voter Log Entries", value:stats.voter_log_count, color:"#374151" },
  ];

  return (
    <Box>
      <Button size="small" startIcon={<RefreshIcon />} onClick={load} sx={{ mb:2, borderRadius:"8px", textTransform:"none", color:"#2f63ad" }}>Refresh</Button>
      <Grid container spacing={2}>
        {items.map(({ label, value, color }) => (
          <Grid item xs={6} sm={4} key={label}>
            <Box sx={{ p:2, borderRadius:"12px", bgcolor:"rgba(47,99,173,0.03)", border:"1px solid rgba(47,99,173,0.08)", textAlign:"center" }}>
              <Typography variant="h4" sx={{ fontWeight:800, color }}>{value ?? 0}</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>{label}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ── Main ────────────────────────────────────────────────────────────
const ConsentTab = ({ election }) => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:44 } }}>
          <Tab label="Member Consent" />
          <Tab label="Election Stats" />
          <Tab label="Webhooks" />
        </Tabs>
      </Paper>
      {tab===0 && (
        <Box>
          <ElectronicVotingConsentList />
          <Divider sx={{ my:3 }} />
          <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>
            Per-Election Manual Overrides
          </Typography>
          <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
            Manually grant or revoke consent for this specific election only, separate from the
            membership system's electronic voting consent above.
          </Typography>
          <ConsentManager electionId={election?.id} election={election} />
        </Box>
      )}
      {tab===1 && <StatsPanel electionId={election?.id} />}
      {tab===2 && <WebhooksPanel />}
    </Box>
  );
};

export default ConsentTab;
